import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../utils/auth';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Scale, FileText, Clock, CheckCircle, LogOut, Plus, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cases = () => {
  const navigate = useNavigate();
  const user = auth.getUser();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      toast.error('Kunne ikke hente saker');
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (caseItem) => {
    const hasAnswer = !!caseItem.lawyer_response;
    const isPaid = caseItem.payment_status === 'COMPLETED';
    
    if (isPaid) {
      return { 
        text: 'Betalt', 
        color: 'bg-slate-100 text-slate-800',
        icon: CheckCircle 
      };
    }
    
    if (hasAnswer) {
      return { 
        text: 'Svar mottatt', 
        color: 'bg-emerald-100 text-emerald-700',
        icon: CheckCircle 
      };
    }
    
    return { 
      text: 'Venter på advokat', 
      color: 'bg-amber-100 text-amber-700',
      icon: Clock 
    };
  };

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Advokat for alle</h1>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-slate-600">Hei, {user?.name}</span>
              <Button 
                onClick={() => {
                  auth.logout();
                  navigate('/');
                }} 
                variant="outline"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logg ut
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Mine saker</h2>
            <p className="text-slate-600">Oversikt over alle dine juridiske saker</p>
          </div>
          <Button
            onClick={() => navigate('/chat')}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Ny sak
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
        ) : cases.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <FileText className="w-20 h-20 text-slate-300 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-slate-900 mb-3">Ingen saker ennå</h3>
            <p className="text-slate-600 mb-6">
              Opprett din første sak for å få juridisk hjelp
            </p>
            <Button
              onClick={() => navigate('/chat')}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              Opprett sak
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {cases.map((caseItem) => {
              const statusInfo = getStatusInfo(caseItem);
              const StatusIcon = statusInfo.icon;
              const hasAnswer = !!caseItem.lawyer_response;
              const isPaid = caseItem.payment_status === 'COMPLETED';
              
              return (
                <div
                  key={caseItem.id}
                  onClick={() => navigate(`/case/${caseItem.id}`)}
                  className="bg-white rounded-xl border-2 border-slate-200 hover:border-slate-300 p-6 cursor-pointer transition-all hover:shadow-lg group shadow-sm"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-slate-900 transition-colors">
                        {caseItem.topic}
                      </h3>
                      <p className="text-slate-600 line-clamp-2 mb-3">
                        {caseItem.intake_summary}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-slate-500">
                        <span>{new Date(caseItem.created_at).toLocaleDateString('no-NO')}</span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 items-end">
                      <span className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-bold ${statusInfo.color}`}>
                        <StatusIcon className="w-4 h-4" />
                        {statusInfo.text}
                      </span>
                      
                      {hasAnswer && !isPaid && (
                        <span className="px-3 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-bold">
                          <Lock className="w-3 h-3 inline mr-1" />
                          Lås opp for 499 kr
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Hint */}
                  <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                    <span className="text-sm text-slate-500">
                      Klikk for å se detaljer
                    </span>
                    {hasAnswer && (
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/case/${caseItem.id}`);
                        }}
                        size="sm"
                        className="bg-slate-900 hover:bg-slate-800"
                      >
                        {isPaid ? 'Se svar' : 'Lås opp svar'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Cases;
